import type { MedplumClient } from '@medplum/core';
import type { Appointment, EpisodeOfCare } from '@medplum/fhirtypes';
import { findExistingGroupEnrollmentEpisodeOfCare } from '@/lib/groupTherapy/groupEnrollment';
import { findEncounterForAppointment } from '@/pages/appointments/utils/fhirHelpers';

export type ResolveGroupPatientEpisodeResult =
  | { episodeId: string; episodeOfCare?: EpisodeOfCare }
  | { episodeId: undefined; errorKind: 'missing-episode' };

type MedplumLike = Parameters<typeof findEncounterForAppointment>[0];

/**
 * Resolve the selected patient's Group Therapy EpisodeOfCare for a shared Appointment.
 * Prefer Encounter.episodeOfCare; fall back to enrollment search by Group.
 */
export async function resolveGroupPatientEpisode(
  medplum: MedplumLike,
  options: {
    appointment: Appointment;
    patientId: string;
    groupId?: string;
  },
): Promise<ResolveGroupPatientEpisodeResult> {
  const { appointment, patientId, groupId } = options;
  if (!appointment.id || !patientId) {
    return { episodeId: undefined, errorKind: 'missing-episode' };
  }

  const encounter = await findEncounterForAppointment(
    medplum,
    appointment.id,
    patientId,
    appointment.start,
  );

  const fromEncounter = encounter?.episodeOfCare?.[0]?.reference;
  if (fromEncounter?.startsWith('EpisodeOfCare/')) {
    const episodeId = fromEncounter.replace('EpisodeOfCare/', '');
    return { episodeId };
  }

  if (groupId) {
    const enrolled = await findExistingGroupEnrollmentEpisodeOfCare(
      medplum as MedplumClient,
      patientId,
      groupId,
    );
    if (enrolled?.id) {
      return { episodeId: enrolled.id, episodeOfCare: enrolled };
    }
  }

  return { episodeId: undefined, errorKind: 'missing-episode' };
}

/** Pure helper for stale-response / selection apply guards (unit-tested). */
export function shouldApplyTreatmentPlanResult(
  requestedPatientId: string,
  selectedPatientId: string | undefined,
): boolean {
  return Boolean(selectedPatientId) && requestedPatientId === selectedPatientId;
}
